import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Controls from "./Controls";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { LineChart } from "@mui/x-charts/LineChart";

function Main() {
  const exactDt = 0.001;
  const [dt, setDt] = useState(0.1);
  const [exactXdata, setExactXdata] = useState([0]);
  const [exactYdata, setExactYdata] = useState([0]);
  const [xdata, setXdata] = useState([[0], [0], [0]]);
  const [ydata, setYdata] = useState([[0], [0], [0]]);

  useEffect(() => {
    const updatedExactYdata = [1];
    const updatedExactXdata = [0];
    for (let i = 1; i < 10 / exactDt; i++) {
      const length = updatedExactYdata.length;
      const k1 =
        Math.sin(updatedExactXdata[length - 1]) ** 2 *
        updatedExactYdata[length - 1];
      const k2 =
        Math.sin(updatedExactXdata[length - 1] + exactDt / 2) ** 2 *
        (updatedExactYdata[length - 1] + (k1 * exactDt) / 2);
      const k3 =
        Math.sin(updatedExactXdata[length - 1] + exactDt / 2) ** 2 *
        (updatedExactYdata[length - 1] + (k2 * exactDt) / 2);
      const k4 =
        Math.sin(updatedExactXdata[length - 1] + exactDt) ** 2 *
        (updatedExactYdata[length - 1] + k3 * exactDt);
      updatedExactXdata.push(updatedExactXdata[length - 1] + exactDt);
      updatedExactYdata.push(
        updatedExactYdata[length - 1] +
          (exactDt * (k1 + 2 * k2 + 2 * k3 + k4)) / 6
      );
    }
    setExactYdata(
      updatedExactYdata.filter((e, i) => i % Math.ceil(dt / exactDt) == 0)
    );
    setExactXdata(
      updatedExactXdata.filter((e, i) => i % Math.ceil(dt / exactDt) == 0)
    );
  }, [dt]);

  useEffect(() => {
    const updatedEulerYdata = [1];
    const updatedEulerXdata = [0];
    const updated2ndYdata = [1];
    const updated2ndXdata = [0];
    const updated4thYdata = [1];
    const updated4thXdata = [0];
    for (let i = 1; i < 10 / dt; i++) {
      const length = updatedEulerYdata.length;
      const eulerY =
        updatedEulerYdata[length - 1] +
        dt *
          Math.sin(updatedEulerXdata[length - 1]) ** 2 *
          updatedEulerYdata[length - 1];
      updatedEulerXdata.push(updatedEulerXdata[length - 1] + dt);
      updatedEulerYdata.push(eulerY);

      const eulerYhalf =
        updated2ndYdata[length - 1] +
        (dt / 2) *
          Math.sin(updated2ndXdata[length - 1]) ** 2 *
          updated2ndYdata[length - 1];
      const Y2nd =
        updated2ndYdata[length - 1] +
        dt * Math.sin(updated2ndXdata[length - 1] + dt / 2) ** 2 * eulerYhalf;
      updated2ndXdata.push(updated2ndXdata[length - 1] + dt);
      updated2ndYdata.push(Y2nd);

      const k1 =
        Math.sin(updated4thXdata[length - 1]) ** 2 *
        updated4thYdata[length - 1];
      const k2 =
        Math.sin(updated4thXdata[length - 1] + dt / 2) ** 2 *
        (updated4thYdata[length - 1] + (k1 * dt) / 2);
      const k3 =
        Math.sin(updated4thXdata[length - 1] + dt / 2) ** 2 *
        (updated4thYdata[length - 1] + (k2 * dt) / 2);
      const k4 =
        Math.sin(updated4thXdata[length - 1] + dt) ** 2 *
        (updated4thYdata[length - 1] + k3 * dt);
      updated4thXdata.push(updated4thXdata[length - 1] + dt);
      updated4thYdata.push(
        updated4thYdata[length - 1] + (dt * (k1 + 2 * k2 + 2 * k3 + k4)) / 6
      );
    }
    setXdata([updatedEulerXdata, updated2ndXdata, updated4thXdata]);
    setYdata([updatedEulerYdata, updated2ndYdata, updated4thYdata]);
  }, [dt]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "column", md: "row" },
        flexGrow: 1,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Controls controls={{ dt, setDt }} />
      </Box>
      <Box
        sx={{
          flex: 2,
          minHeight: 600,
          backgroundColor: "#F5F5F5",
          position: "relative",
          height: "100%",
        }}
      >
        <LineChart
          xAxis={[
            { data: exactXdata },
            { data: xdata[0] },
            { data: xdata[1] },
            { data: xdata[2] },
          ]}
          series={[
            {
              label: "Exact Solution",
              data: exactYdata,
              showMark: false,
            },
            {
              label: "Euler Method",
              data: ydata[0],
            },
            {
              label: "Modified Euler Method",
              data: ydata[1],
            },
            {
              label: "RK4 Method",
              data: ydata[2],
            },
          ]}
        />
      </Box>
    </Box>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
}

function NumericalMethodsODE(props: TabPanelProps) {
  const { children, ...other } = props;

  return (
    <HelmetProvider>
      <Main />
      <Helmet>
        <title>
          Visualization of Numerical Methods for Ordinary Differential Equations
          (ODE)
        </title>
        <meta
          name="description"
          content="Explore interactive visualization of Numerical Methods for Ordinary Differential Equations (ODEs): Understand the differences between Euler Method, RK2, and RK4 with real-time simulation. Set the delta t parameter and instantly see the effects on results. Ideal for learning and visualizing numerical ODE solving techniques."
        />
      </Helmet>
    </HelmetProvider>
  );
}

export default NumericalMethodsODE;
